import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Check, Star, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PaymentModal } from '@/components/payment/PaymentModal';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';

const Pricing = () => {
  const [, setLocation] = useLocation();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  // Check for payment cancellation in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'cancelled') {
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. You can try again anytime.",
        variant: "default",
      });
    }
  }, [toast]);

  const features = [
    "Unlimited email processing",
    "Advanced player database",
    "Smart shortlists & filters",
    "AI-powered data extraction",
    "Email service integration",
    "Export capabilities",
    "ID Camp reach-out management",
    "Automated email sending & tracking"
  ];

  const monthlyPrice = 130;
  const annualPriceMonthly = 100;
  const annualPriceTotal = 1200;

  const handleGetStarted = () => {
    if (user) {
      setShowPaymentModal(true);
    } else {
      setLocation('/login');
    }
  };

  const handleBackToDashboard = () => {
    setLocation('/dashboard?from=pricing');
  };

  return (
    <div className="h-screen overflow-auto bg-gradient-to-br from-gray-50 via-white to-cyan-50/30 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-20"
          style={{
            background: `
              radial-gradient(circle at 20% 30%, rgba(6, 182, 212, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)
            `
          }}
        />
        
        {/* Floating gradient orbs */}
        <div className="absolute -top-20 -left-20 w-96 h-96 opacity-10">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.4) 0%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
        </div>
        
        <div className="absolute -bottom-20 -right-20 w-96 h-96 opacity-10">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%)',
              filter: 'blur(60px)'
            }}
          />
        </div>
      </div>

      {/* Back button */}
      <div className="relative z-10 container mx-auto px-4 pt-6">
        <Button
          variant="ghost"
          onClick={handleBackToDashboard}
          className="mb-4 hover:bg-white/50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-4 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the plan that works best for your program
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <div className="relative group">
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200 relative overflow-hidden transform transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:border-cyan-200">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-transparent to-gray-50/50 opacity-50"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Monthly Plan</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      ${monthlyPrice}
                    </span>
                    <span className="text-gray-600">/month*</span>
                  </div>
                  <span className="text-sm text-gray-500 mt-1">
                    Pay as you go
                  </span>
                </div>

                <ul className="space-y-2 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={handleGetStarted}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>

          {/* Annual Plan */}
          <div className="relative group">
            {/* Popular badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
              <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                <Star className="w-3 h-3 fill-current" />
                MOST POPULAR
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-cyan-100 relative overflow-hidden transform transition-all duration-300 hover:scale-[1.01] hover:shadow-xl">
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-transparent to-teal-50/50 opacity-50"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Annual Plan</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold text-gray-900">
                      ${annualPriceMonthly}
                    </span>
                    <span className="text-gray-600">/month*</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    ${annualPriceTotal} billed annually
                    <span className="text-emerald-600 font-semibold ml-2">
                      Save 23%
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={handleGetStarted}
                  className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-gray-500">
            * Taxes not included
          </p>
          <p className="text-sm text-gray-600">
            Choice of either plan is required to use the platform
          </p>
        </div>

        {/* Enterprise callout */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
            <span className="text-xs">Need a custom solution for your athletic department?</span>
            <a href="mailto:lucas@bloomscouting.com,daniel@bloomscouting.com,gfmorone@berkeley.edu?subject=Custom%20Solution%20Inquiry" className="text-cyan-600 hover:text-cyan-700 font-medium text-xs underline underline-offset-2">
              Contact us
            </a>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal 
        open={showPaymentModal} 
        onOpenChange={setShowPaymentModal}
      />
    </div>
  );
};

export default Pricing; 